import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Award, Store, ExternalLink, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CreatorAvatar from '@/components/CreatorAvatar';

/**
 * Creator of the Week spotlight — used on the public Home page and the
 * Buyer-side Marketplace Preview. Pulls from GET /api/creator-of-the-week
 * and renders nothing if no creator is currently featured (launch-safe
 * earnings flag, empty queue, etc).
 *
 * Pass `creator` directly to skip the fetch (lets parent control loading
 * state). Otherwise the component fetches on mount.
 */
export const CreatorOfTheWeekCard = ({ creator: creatorProp = null, className = '' }) => {
  const navigate = useNavigate();
  const [creator, setCreator] = useState(creatorProp);
  const [loaded, setLoaded] = useState(creatorProp !== null);

  useEffect(() => {
    if (creatorProp !== null) {
      setCreator(creatorProp);
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/creator-of-the-week');
        if (!cancelled && res.data) setCreator(res.data);
      } catch (err) {
        // Silent fail — section auto-hides when creator is null
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [creatorProp]);

  if (!loaded || !creator) return null;

  return (
    <section
      className={`container mx-auto px-4 md:px-12 py-10 md:py-16 border-t border-white/10 ${className}`}
      data-testid="creator-of-the-week-section"
    >
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #0a1a1f 50%, #0a1628 100%)' }}
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#00F0FF]/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#00FF94]/5 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative z-10 p-6 md:p-10 lg:p-12">
          <div className="flex items-center gap-2 mb-6">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#00F0FF]/30 bg-[#00F0FF]/10">
              <Award className="w-4 h-4 text-[#00F0FF]" />
              <span className="text-xs font-semibold tracking-wider uppercase text-[#00F0FF]">
                Creator of the Week
              </span>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Creator info */}
            <div className="flex-shrink-0 lg:w-[320px]">
              <div className="flex items-center gap-4 mb-4">
                <CreatorAvatar
                  storeName={creator.store_name}
                  avatarUrl={creator.avatar}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-xl flex-shrink-0"
                />
                <div>
                  <h3
                    className="text-xl md:text-2xl font-bold text-white"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {creator.store_name}
                  </h3>
                  {creator.business_name && (
                    <p className="text-neutral-400 text-sm">{creator.business_name}</p>
                  )}
                </div>
              </div>

              {creator.bio && (
                <p className="text-neutral-300 text-sm leading-relaxed mb-5 line-clamp-3">
                  {creator.bio}
                </p>
              )}

              {creator.quote && (
                <div className="mb-5 pl-3 border-l-2 border-[#00F0FF]/40">
                  <p className="text-neutral-400 text-sm italic">"{creator.quote}"</p>
                </div>
              )}

              <div className="flex items-center gap-4 mb-5">
                <div className="text-center">
                  <p className="text-lg font-bold text-white">{creator.product_count || 0}</p>
                  <p className="text-[10px] uppercase tracking-wider text-neutral-500">Products</p>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div className="text-center">
                  <p className="text-lg font-bold text-white">
                    {creator.avg_rating > 0 ? creator.avg_rating : '—'}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-neutral-500">Avg Rating</p>
                </div>
                <div className="w-px h-8 bg-white/10" />
                <div className="text-center">
                  <p className="text-lg font-bold text-white">
                    ${Math.round(creator.total_revenue || 0).toLocaleString()}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-neutral-500">Revenue</p>
                </div>
              </div>

              <Button
                onClick={() => navigate(`/store/${creator.store_slug || creator.creator_id}`)}
                className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-semibold rounded-lg px-5 py-2 text-sm gap-2"
                data-testid="cotw-visit-store-btn"
              >
                <Store className="w-4 h-4" />
                Visit Store
                <ExternalLink className="w-3 h-3" />
              </Button>
            </div>

            {/* Top products */}
            {creator.top_products && creator.top_products.length > 0 && (
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wider text-neutral-500 mb-3 font-semibold">
                  Top Products
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {creator.top_products.map((product) => (
                    <div
                      key={product.product_id}
                      onClick={() => navigate(`/product/${product.product_id}`)}
                      className="group rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-[#00F0FF]/30 overflow-hidden cursor-pointer transition-all duration-200"
                      data-testid={`cotw-product-${product.product_id}`}
                    >
                      <div className="aspect-[16/10] bg-neutral-800/50 overflow-hidden">
                        <img
                          src={
                            product.images?.[0] ||
                            'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400'
                          }
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.src =
                              'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400';
                          }}
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="font-semibold text-sm text-white mb-1 line-clamp-1 group-hover:text-[#00F0FF] transition-colors">
                          {product.name}
                        </h4>
                        <div className="flex items-center justify-between">
                          <span className="text-[#00F0FF] font-bold text-sm">${product.price}</span>
                          <div className="flex items-center gap-1 text-xs text-neutral-500">
                            {product.rating > 0 && (
                              <>
                                <Star className="w-3 h-3 fill-[#00F0FF] text-[#00F0FF]" />
                                <span>{product.rating}</span>
                              </>
                            )}
                          </div>
                        </div>
                        {product.category && (
                          <span className="inline-block mt-1.5 text-[10px] px-2 py-0.5 rounded-full bg-[#00F0FF]/10 text-[#00F0FF]/70">
                            {product.category}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreatorOfTheWeekCard;
